import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { Howl } from 'howler';

// Define the sound files. You will need to add these to /public/sounds/
const soundFiles = {
  click: '/sounds/click.mp3',
  hover: '/sounds/hover.mp3',
  drag: '/sounds/drag.mp3',
  transition: '/sounds/transition.mp3',
  tornado: '/sounds/tornado.mp3',
};

type SoundType = keyof typeof soundFiles;

interface SoundContextType {
  isSoundEnabled: boolean;
  toggleSound: () => void;
  playSound: (sound: SoundType) => void;
}

const SoundContext = createContext<SoundContextType | undefined>(undefined);

export const SoundProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isSoundEnabled, setIsSoundEnabled] = useState(false);

  const sounds = useMemo(() => {
    if (typeof window === 'undefined') return {};
    return {
      click: new Howl({ src: [soundFiles.click], volume: 0.7 }),
      hover: new Howl({ src: [soundFiles.hover], volume: 0.3 }),
      drag: new Howl({ src: [soundFiles.drag], volume: 0.6 }),
      transition: new Howl({ src: [soundFiles.transition], volume: 0.5 }),
      tornado: new Howl({ 
        src: [soundFiles.tornado], 
        volume: 0.7,
        loop: true,
        preload: true
      }),
    };
  }, []);

  const toggleSound = useCallback(() => {
    setIsSoundEnabled(prev => {
      const newState = !prev;
      if (newState) {
        sounds.transition?.play();
        sounds.tornado?.play();
      } else {
        sounds.tornado?.stop();
      }
      return newState;
    });
  }, [sounds.transition, sounds.tornado]);

  const playSound = useCallback((sound: SoundType) => {
    if (isSoundEnabled && sounds[sound]) {
      (sounds[sound] as Howl).play();
    }
  }, [isSoundEnabled, sounds]);

  const value = { isSoundEnabled, toggleSound, playSound };

  return (
    <SoundContext.Provider value={value}>
      {children}
    </SoundContext.Provider>
  );
};

export const useSound = () => {
  const context = useContext(SoundContext);
  if (context === undefined) {
    throw new Error('useSound must be used within a SoundProvider');
  }
  return context;
};
